import React, { useCallback, useState, useEffect } from "react";
import styled from "styles/styled_components";
import { Maybe } from "true-myth";

import {
  IGracePeriodDropdownOption,
  GRACE_PERIODS,
  getSelectedGracePeriodOption,
} from "helpers/settings/schedule_enforcement_section";

import CheckboxWithLabel from "components/shared/checkbox_with_label";
import LabeledDropdown, {
  DROPDOWN_LABEL_POSITION,
} from "components/shared/labeled_dropdown";

const StyledSettingsSection = styled.div`
  margin: ${(props) => props.theme.spacing.small};
`;

const StyledSettingsHeader = styled.h2`
  font-size: ${(props) => props.theme.text.size.base};
  font-family: ${(props) => props.theme.text.mediumFamily};
  margin-top: ${(props) => props.theme.spacing.medium};
  margin-bottom: ${(props) => props.theme.spacing.medium};
`;

const StyledSettingsInfo = styled.div`
  font-size: ${(props) => props.theme.text.size.base};
  margin-top: ${(props) => props.theme.spacing.small};
  margin-bottom: ${(props) => props.theme.spacing.small};
`;

const StyledSettingsControl = styled.div`
  margin-top: ${(props) => props.theme.spacing.medium};
  margin-bottom: ${(props) => props.theme.spacing.medium};
`;

interface IScheduleEnforcementSectionProps {
  enforcementOn: boolean;
  gracePeriod: number;
  permalink: string;
  setEnforcementSettings: (
    permalink: string,
    enforcementOn: boolean,
    gracePeriod: number
  ) => void;
}

function ScheduleEnforcementSection({
  enforcementOn,
  gracePeriod,
  permalink,
  setEnforcementSettings,
}: IScheduleEnforcementSectionProps) {
  const [enforcementOnChecked, setEnforcementOnChecked] =
    useState(enforcementOn);

  useEffect(() => {
    setEnforcementOnChecked(enforcementOn);
  }, [enforcementOn]);

  const handleGracePeriodClick = useCallback(
    (gracePeriodOption: Maybe<IGracePeriodDropdownOption>) => {
      gracePeriodOption.match({
        Just: (selectedGracePeriodOption) => {
          setEnforcementSettings(
            permalink,
            enforcementOn,
            selectedGracePeriodOption.meta
          );
        },
        Nothing: () => {
          return;
        },
      });
    },
    [enforcementOn]
  );

  let gracePeriodDropdown;
  if (enforcementOnChecked) {
    gracePeriodDropdown = (
      <StyledSettingsControl>
        <LabeledDropdown
          dropdownName="gracePeriod"
          label="Grace Period"
          options={GRACE_PERIODS}
          currentOption={getSelectedGracePeriodOption(gracePeriod)}
          onOptionClick={handleGracePeriodClick}
          labelPosition={DROPDOWN_LABEL_POSITION.TOP}
        />
      </StyledSettingsControl>
    );
  }

  return (
    <StyledSettingsSection>
      <StyledSettingsHeader>Schedule Enforcement</StyledSettingsHeader>
      <StyledSettingsInfo>
        With schedule enforcement enabled, if an employee isn't scheduled for a
        shift, they will need manager permission to clock in. You can also
        select the grace period you want to give employees for clocking in early
        or late.
      </StyledSettingsInfo>
      <StyledSettingsControl>
        <CheckboxWithLabel
          label="Prevent early clock-in at the POS"
          checked={enforcementOnChecked}
          onChangeHandler={() => {
            setEnforcementSettings(
              permalink,
              !enforcementOnChecked,
              gracePeriod
            );
            setEnforcementOnChecked(!enforcementOnChecked);
          }}
          disabled={false}
        />
      </StyledSettingsControl>
      {gracePeriodDropdown}
    </StyledSettingsSection>
  );
}

export default ScheduleEnforcementSection;
